import { useState, useRef, useEffect, useMemo } from "react";
import { useNarrative, useUpdateNarrative, useIngestNarrative } from "@/hooks/useApi";
import type { NarrativeCaseStudy } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";

type ChecklistItem = { id: string; label: string; done: boolean };

function normalizeCaseStudies(value: unknown): NarrativeCaseStudy[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "string") {
        const title = item.trim();
        return title ? { title, summary: title } : null;
      }
      if (!item || typeof item !== "object") return null;
      const obj = item as unknown as Record<string, unknown>;
      const title = typeof obj.title === "string" ? obj.title.trim() : "";
      const summary = typeof obj.summary === "string" ? obj.summary.trim() : "";
      const metrics = typeof obj.metrics === "string" ? obj.metrics.trim() : undefined;
      if (!title && !summary) return null;
      return {
        title: title || summary.slice(0, 80),
        summary: summary || title,
        ...(metrics ? { metrics } : {}),
      };
    })
    .filter((x): x is NarrativeCaseStudy => Boolean(x));
}

export function NarrativePage() {
  const { data: narrative, isLoading, error } = useNarrative();
  const updateNarrative = useUpdateNarrative();
  const ingestNarrative = useIngestNarrative();

  const [content, setContent] = useState("");
  const [productsText, setProductsText] = useState("");
  const [icpSummary, setIcpSummary] = useState("");
  const [proofPointsText, setProofPointsText] = useState("");
  const [caseStudies, setCaseStudies] = useState<NarrativeCaseStudy[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [initialized, setInitialized] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [ingestStatus, setIngestStatus] = useState<"idle" | "running" | "done" | "error">("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);

  function applyNarrative(data: {
    content?: string;
    products?: string[];
    icpSummary?: string;
    proofPoints?: string[];
    caseStudies?: unknown;
  }) {
    const nextContent = data.content ?? "";
    const nextProducts = (data.products ?? []).join("\n");
    const nextIcp = data.icpSummary ?? "";
    const nextProof = (data.proofPoints ?? []).join("\n");
    const nextCases = normalizeCaseStudies(data.caseStudies);
    setContent(nextContent);
    setProductsText(nextProducts);
    setIcpSummary(nextIcp);
    setProofPointsText(nextProof);
    setCaseStudies(nextCases);
    setSavedSnapshot(
      JSON.stringify({
        content: nextContent,
        productsText: nextProducts,
        icpSummary: nextIcp,
        proofPointsText: nextProof,
        caseStudies: nextCases,
      }),
    );
  }

  useEffect(() => {
    if (narrative && !initialized) {
      applyNarrative(narrative);
      setInitialized(true);
    }
  }, [narrative, initialized]);

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        content,
        productsText,
        icpSummary,
        proofPointsText,
        caseStudies,
      }),
    [content, productsText, icpSummary, proofPointsText, caseStudies],
  );

  const hasUnsavedChanges = initialized && currentSnapshot !== savedSnapshot;

  const products = productsText
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const proofPoints = proofPointsText
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  const checklist: ChecklistItem[] = [
    { id: "content", label: "Narrative content", done: content.trim().length > 80 },
    { id: "icp", label: "ICP summary", done: icpSummary.trim().length > 0 },
    { id: "products", label: "≥1 product", done: products.length >= 1 },
    { id: "cases", label: "≥1 case study", done: caseStudies.length >= 1 },
    { id: "proof", label: "Proof points", done: proofPoints.length >= 1 },
  ];
  const completenessPct = Math.round(
    (checklist.filter((c) => c.done).length / checklist.length) * 100,
  );

  const wordCount = content ? content.split(/\s+/).filter(Boolean).length : 0;
  const charCount = content.length;

  async function handleSave() {
    setSaveStatus("saving");
    try {
      await updateNarrative.mutateAsync({
        content,
        products,
        icpSummary,
        proofPoints,
        caseStudies,
      });
      setSavedSnapshot(currentSnapshot);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target?.result as string;
      if (!text) return;
      setIngestStatus("running");
      try {
        const result = await ingestNarrative.mutateAsync(text);
        applyNarrative(result);
        setInitialized(true);
        setIngestStatus("done");
        setTimeout(() => setIngestStatus("idle"), 2500);
      } catch {
        setContent(text);
        setIngestStatus("error");
        setTimeout(() => setIngestStatus("idle"), 3000);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function handleDownload() {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "narrative.md";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleRevert() {
    try {
      const snap = JSON.parse(savedSnapshot) as {
        content: string;
        productsText: string;
        icpSummary: string;
        proofPointsText: string;
        caseStudies: NarrativeCaseStudy[];
      };
      setContent(snap.content);
      setProductsText(snap.productsText);
      setIcpSummary(snap.icpSummary);
      setProofPointsText(snap.proofPointsText);
      setCaseStudies(snap.caseStudies);
    } catch {
      /* ignore */
    }
  }

  function updateCaseStudy(index: number, patch: Partial<NarrativeCaseStudy>) {
    setCaseStudies((prev) =>
      prev.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    );
  }

  function addCaseStudy() {
    setCaseStudies((prev) => [...prev, { title: "", summary: "" }]);
  }

  function removeCaseStudy(index: number) {
    setCaseStudies((prev) => prev.filter((_, i) => i !== index));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
          <p className="text-sm text-muted-foreground">Loading narrative…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive">
          Failed to load narrative. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <>
      <header className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Company Narrative</h1>
            <p className="text-muted-foreground">
              Structured knowledge base plus markdown story used across qualification and outreach.
            </p>
          </div>
          <div className="hidden items-center gap-3 rounded-md border border-border px-3 py-1.5 text-xs text-muted-foreground sm:flex">
            <span>
              <strong className="text-foreground">{wordCount}</strong> words
            </span>
            <span className="text-border">|</span>
            <span>
              <strong className="text-foreground">{charCount}</strong> chars
            </span>
          </div>
        </div>
      </header>

      <div className="mb-6 rounded-lg border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">Completeness</h2>
          <span className="text-sm font-medium text-foreground">{completenessPct}%</span>
        </div>
        <div className="mb-3 h-2 overflow-hidden rounded-full bg-muted/20">
          <div
            className="h-full rounded-full bg-brand transition-all"
            style={{ width: `${completenessPct}%` }}
          />
        </div>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {checklist.map((item) => (
            <li
              key={item.id}
              className={cn(
                "flex items-center gap-2 text-xs",
                item.done ? "text-emerald-400" : "text-muted-foreground",
              )}
            >
              <span
                className={cn(
                  "inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px]",
                  item.done
                    ? "border-emerald-500/50 bg-emerald-500/20"
                    : "border-border",
                )}
              >
                {item.done ? "✓" : ""}
              </span>
              {item.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-medium text-foreground">Structured KB</h3>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs text-muted-foreground">ICP summary</span>
              <textarea
                value={icpSummary}
                onChange={(e) => setIcpSummary(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                placeholder="Target segment, company size, buyer personas…"
              />
            </label>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs text-muted-foreground">Products (one per line)</span>
              <textarea
                value={productsText}
                onChange={(e) => setProductsText(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                placeholder="Platform X&#10;Services Y"
              />
            </label>

            <label className="mb-3 block">
              <span className="mb-1 block text-xs text-muted-foreground">Proof points (one per line)</span>
              <textarea
                value={proofPointsText}
                onChange={(e) => setProofPointsText(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-transparent px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                placeholder="40% faster time-to-value&#10;3x pipeline growth"
              />
            </label>

            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Case studies</span>
              <button
                type="button"
                onClick={addCaseStudy}
                className="text-xs font-medium text-brand hover:underline"
              >
                Add
              </button>
            </div>
            <div className="space-y-3">
              {caseStudies.length === 0 && (
                <p className="text-xs text-muted-foreground">No case studies yet.</p>
              )}
              {caseStudies.map((cs, index) => (
                <div key={index} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <input
                      value={cs.title}
                      onChange={(e) => updateCaseStudy(index, { title: e.target.value })}
                      className="w-full rounded-md border border-border bg-transparent px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                      placeholder="Title"
                    />
                    <button
                      type="button"
                      onClick={() => removeCaseStudy(index)}
                      className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                    >
                      Remove
                    </button>
                  </div>
                  <textarea
                    value={cs.summary}
                    onChange={(e) => updateCaseStudy(index, { summary: e.target.value })}
                    rows={2}
                    className="mb-2 w-full rounded-md border border-border bg-transparent px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Summary"
                  />
                  <input
                    value={cs.metrics ?? ""}
                    onChange={(e) => updateCaseStudy(index, { metrics: e.target.value })}
                    className="w-full rounded-md border border-border bg-transparent px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                    placeholder="Metrics (optional)"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 text-sm font-medium text-foreground">Upload / ingest</h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Import a deck or one-pager. AI ingest fills structured fields when available.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={ingestNarrative.isPending}
                className="flex-1 rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground transition hover:bg-muted/20 disabled:opacity-50"
              >
                {ingestStatus === "running"
                  ? "Ingesting…"
                  : ingestStatus === "done"
                    ? "Ingested"
                    : ingestStatus === "error"
                      ? "Ingest failed — pasted locally"
                      : "Choose file"}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground transition hover:bg-muted/20"
                title="Download as .md"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,.txt,.html,.pdf"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Markdown Editor
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleRevert}
                  disabled={!hasUnsavedChanges}
                  className="rounded px-2 py-1 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                >
                  Revert
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={updateNarrative.isPending || !hasUnsavedChanges}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition",
                    saveStatus === "saved"
                      ? "bg-success/20 text-success"
                      : saveStatus === "error"
                        ? "bg-destructive/20 text-destructive"
                        : "bg-brand text-brand-foreground hover:opacity-90",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  {updateNarrative.isPending && (
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  )}
                  {saveStatus === "saved"
                    ? "Saved!"
                    : saveStatus === "error"
                      ? "Error saving"
                      : updateNarrative.isPending
                        ? "Saving…"
                        : "Save"}
                </button>
              </div>
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[560px] w-full resize-y border-0 bg-transparent p-4 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              placeholder="Paste or write your narrative in Markdown format…

# Company Name

## ICP
Target industries, company size, buyer personas…

## Products
Core offerings and value propositions…

## Proof points
Metrics, outcomes, differentiators…

## Case studies
Customer wins and references…"
              spellCheck
            />
            <div className="flex items-center justify-between border-t border-border px-4 py-2">
              <div className="flex items-center gap-3 sm:hidden">
                <span className="text-xs text-muted-foreground">{wordCount} words</span>
                <span className="text-xs text-muted-foreground">{charCount} chars</span>
              </div>
              <div />
              {hasUnsavedChanges && (
                <span className="text-xs text-warning">Unsaved changes</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
